import { h } from '../utils/dom.ts';
import { getLocale, LOCALE_LABELS, setLocale, t, isLocale } from '../i18n/index.ts';
import { LOCALES, type Business } from '../utils/validation.ts';
import { tenantAsset } from '../services/tenant.ts';

export type NavKey = 'book' | 'my';

export function languageSwitcher(): HTMLElement {
  const select = h('select', {
    class: 'lang-select',
    'aria-label': t('nav.language'),
    onChange: (e: Event) => {
      const v = (e.target as HTMLSelectElement).value;
      if (isLocale(v)) setLocale(v);
    },
  }, LOCALES.map((l) => h('option', { value: l, selected: l === getLocale() }, LOCALE_LABELS[l])));
  return select;
}

/** App shell for a tenant: branded header, page content, bottom navigation. */
export function tenantShell(business: Business, active: NavKey | null, content: Node): HTMLElement {
  const base = `/s/${business.slug}`;
  const navItem = (key: NavKey, href: string, label: string) =>
    h('a', { href, class: `nav-item${key === active ? ' active' : ''}`, 'data-link': true, 'aria-current': key === active ? 'page' : undefined }, label);

  return h('div', { class: 'shell' },
    h('header', { class: 'topbar' },
      h('a', { href: `${base}/`, class: 'brand', 'data-link': true },
        business.logo
          ? h('img', { src: tenantAsset(business.slug, business.logo), alt: '', width: 36, height: 36, class: 'brand-logo' })
          : h('span', { class: 'brand-logo brand-initial', 'aria-hidden': 'true' }, business.short_name.slice(0, 1)),
        h('span', { class: 'brand-text' },
          h('strong', null, business.short_name),
          h('small', null, business.location.city),
        ),
      ),
      languageSwitcher(),
    ),
    h('main', { class: 'content', id: 'main' }, content, legalFooter(base)),
    h('nav', { class: 'bottom-nav', 'aria-label': 'Main' },
      navItem('book', `${base}/`, t('nav.book')),
      navItem('my', `${base}/me`, t('nav.my')),
    ),
  );
}

/** Impressum + Datenschutz links: legally required on every page in Germany. */
function legalFooter(base: string): HTMLElement {
  return h('footer', { class: 'legal-footer' },
    h('a', { href: `${base}/impressum`, 'data-link': true }, t('footer.imprint')),
    h('a', { href: `${base}/datenschutz`, 'data-link': true }, t('footer.privacy')),
  );
}

/** Shell for platform-level pages (landing, platform Impressum/Datenschutz). */
export function platformShell(content: Node): HTMLElement {
  return h('div', { class: 'shell' },
    h('header', { class: 'topbar' }, h('a', { href: '/', class: 'brand', 'data-link': true }, h('strong', null, 'termin.quest')), languageSwitcher()),
    h('main', { class: 'content page', id: 'main' }, content, legalFooter('')),
  );
}

/** Applies the tenant's brand colours as CSS custom properties. */
export function applyTheme(business: Business | null): void {
  const root = document.documentElement.style;
  if (!business) {
    root.removeProperty('--brand');
    root.removeProperty('--accent');
    headLink('manifest', '/manifest.webmanifest');
    headLink('apple-touch-icon', '/icons/apple-touch-icon.png');
    return;
  }
  root.setProperty('--brand', business.theme.primary);
  root.setProperty('--accent', business.theme.accent ?? business.theme.primary);
  document.querySelector('meta[name="theme-color"]')?.setAttribute('content', business.theme.primary);
  document.title = `${business.name} · ${t('app.title')}`;

  // Each shop installs as its own app with its own icon.
  headLink('manifest', `/tenants/${business.slug}/manifest.webmanifest`);
  headLink('apple-touch-icon', `/tenants/${business.slug}/apple-touch-icon.png`);
}

function headLink(rel: string, href: string): void {
  let link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    document.head.append(link);
  }
  if (link.getAttribute('href') !== href) link.href = href;
}
